/**
 * Native Tooltip Bridge — the enforcement half of the global tooltip system.
 *
 * Wherever the suite primitives are loaded, the browser's default `title`
 * tooltip is retired: every `title` attribute that reaches the DOM — static
 * markup, a component forwarding a prop, third-party widgets, future code —
 * is captured at hover start (before the browser could ever show its own),
 * stripped, and re-served as a suite tooltip: same `.suite-ui-tooltip`
 * surface, same central timing config (`tooltipConfig.ts`). Stripped values
 * are kept in `data-suite-hint` and, when the element has no accessible
 * name, mirrored into `aria-label`. Frames (`iframe`, `frame`) never anchor
 * a suite tooltip — their native `title` is retired like any other, with the
 * name mirrored to `aria-label` so the embedded document stays named.
 *
 * The bridge is dependency-free DOM code (no React), idempotent, and
 * installs itself when the primitives module loads in a DOM environment.
 * `uninstallNativeTooltipBridge()` restores stock browser behavior.
 */

import {
    getTooltipConfig,
    subscribeTooltipConfig,
} from './tooltipConfig';
import { placeTooltipInViewport } from './tooltipPlacement';

const HINT_DATA_ATTR = 'data-suite-hint';
const ANCHOR_SELECTOR = '[title], [' + HINT_DATA_ATTR + ']';
/** Embedded documents never anchor a tooltip: a frame's `title` names the
 *  document for assistive tech, not hovering chrome — and because events never
 *  cross the frame boundary, the pointer never "leaves", so a frame anchor
 *  would pin its bubble (top-clamped, centered on the full-width frame) to the
 *  top of the screen for the whole session. */
const FRAME_SELECTOR = 'iframe, frame';

let installed = false;
let bridgeEl: HTMLDivElement | null = null;
let anchor: Element | null = null;
let openTimer: number | null = null;
let closeTimer: number | null = null;
let lastClosedAt = 0;
let scrollHandler: (() => void) | null = null;
let unsubscribeConfig: (() => void) | null = null;

function hideBridge(): void {
    if (openTimer !== null) {
        window.clearTimeout(openTimer);
        openTimer = null;
    }
    if (closeTimer !== null) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
    }
    if (bridgeEl !== null) bridgeEl.remove();
    anchor = null;
}

function ensureBridgeEl(): HTMLDivElement {
    if (bridgeEl === null) {
        bridgeEl = document.createElement('div');
        bridgeEl.className = 'suite-ui-tooltip';
        bridgeEl.setAttribute('role', 'tooltip');
        // The anchor carries the accessible name (aria-label); the floating
        // bubble is purely visual for screen readers.
        bridgeEl.setAttribute('aria-hidden', 'true');
        bridgeEl.style.position = 'fixed';
        bridgeEl.style.pointerEvents = 'none';
        bridgeEl.style.zIndex = '120';
        bridgeEl.style.display = 'none';
        document.body.appendChild(bridgeEl);
    }
    return bridgeEl;
}

function positionBridge(): void {
    if (bridgeEl === null || anchor === null || !anchor.isConnected) return;
    const config = getTooltipConfig();
    const rect = anchor.getBoundingClientRect();
    const size = bridgeEl.getBoundingClientRect();
    // The shared global rule: flip below when the window has no room above
    // the anchor (top-bar triggers), clamp inside the window — the bubble
    // can never cover the pointer sitting on the anchor.
    const placement = placeTooltipInViewport({
        anchor: rect,
        width: size.width,
        height: size.height,
        side: config.side,
        sideOffset: config.sideOffset,
    });
    bridgeEl.style.left = `${placement.left}px`;
    bridgeEl.style.top = `${placement.top}px`;
}

function openBridge(el: Element, text: string): void {
    if (closeTimer !== null) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
    }
    if (anchor === el && bridgeEl !== null && bridgeEl.style.display !== 'none') return;
    if (anchor !== null && anchor !== el) {
        // Moving to another anchor: the previous bubble closes right away
        if (openTimer !== null) {
            window.clearTimeout(openTimer);
            openTimer = null;
        }
        hideBridge();
    }
    anchor = el;
    if (openTimer !== null) return;
    const config = getTooltipConfig();
    if (config.disabled) return;
    const skip = Date.now() - lastClosedAt < config.skipDelayDuration;
    const wait = skip ? 0 : config.delayDuration;
    const show = () => {
        openTimer = null;
        const el2 = anchor;
        if (!el2 || !el2.isConnected) return;
        const bubble = ensureBridgeEl();
        bubble.textContent = el2.getAttribute(HINT_DATA_ATTR) ?? text;
        bubble.style.display = 'block';
        positionBridge();
    };
    if (wait === 0) show();
    else openTimer = window.setTimeout(show, wait);
}

function scheduleClose(): void {
    if (openTimer !== null) {
        // Left before the open delay fired: cancel silently
        window.clearTimeout(openTimer);
        openTimer = null;
        return;
    }
    if (anchor === null || closeTimer !== null) return;
    const grace = getTooltipConfig().closeDelayDuration;
    if (grace <= 0) {
        lastClosedAt = Date.now();
        hideBridge();
        return;
    }
    closeTimer = window.setTimeout(() => {
        closeTimer = null;
        lastClosedAt = Date.now();
        hideBridge();
    }, grace);
}

/** Retire a frame's native title tooltip (the browser would surface it on
 *  hover) while keeping its accessible name: the title is mirrored into
 *  `aria-label` — a frame has no text content, so the mirror is unconditional
 *  on it. The stored hint is deliberately NOT re-served: a frame never anchors
 *  a suite tooltip (see FRAME_SELECTOR). */
function retireFrameTitle(frame: Element): void {
    const raw = frame.getAttribute('title');
    if (raw === null) return;
    frame.removeAttribute('title');
    if (raw.trim() === '') return;
    frame.setAttribute(HINT_DATA_ATTR, raw);
    if (!frame.getAttribute('aria-label') && !frame.getAttribute('aria-labelledby')) {
        frame.setAttribute('aria-label', raw);
    }
}

function handleOver(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const el = target.closest(ANCHOR_SELECTOR);
    if (el !== null && el.matches(FRAME_SELECTOR)) {
        retireFrameTitle(el);
        if (anchor !== null) scheduleClose();
        return;
    }
    if (el === null) {
        if (anchor !== null) scheduleClose();
        return;
    }
    // Retire the native tooltip before the browser can ever show it
    const raw = el.getAttribute('title');
    if (raw !== null) {
        if (raw.trim() === '') {
            el.removeAttribute('title');
            return;
        }
        el.setAttribute(HINT_DATA_ATTR, raw);
        el.removeAttribute('title');
        if (
            !el.getAttribute('aria-label') &&
            !el.getAttribute('aria-labelledby') &&
            !(el.textContent ?? '').trim()
        ) {
            el.setAttribute('aria-label', raw);
        }
    }
    const stored = el.getAttribute(HINT_DATA_ATTR);
    if (stored) openBridge(el, stored);
}

function handleOut(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (anchor === null) return;
    // Only a real leave from the anchor starts the close grace
    const related = event.relatedTarget;
    if (related instanceof Element && anchor.contains(related)) return;
    if (target.closest(ANCHOR_SELECTOR) === anchor) scheduleClose();
}

function handleFocusIn(event: FocusEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const el = target.closest(ANCHOR_SELECTOR);
    if (el === null) return;
    if (el.matches(FRAME_SELECTOR)) {
        retireFrameTitle(el);
        return;
    }
    const raw = el.getAttribute('title');
    if (raw !== null) {
        if (raw.trim() === '') {
            el.removeAttribute('title');
            return;
        }
        el.setAttribute(HINT_DATA_ATTR, raw);
        el.removeAttribute('title');
        if (!el.getAttribute('aria-label') && !el.getAttribute('aria-labelledby')) {
            el.setAttribute('aria-label', raw);
        }
    }
    const stored = el.getAttribute(HINT_DATA_ATTR);
    if (stored) openBridge(el, stored);
}

function handleFocusOut(): void {
    if (anchor !== null) scheduleClose();
}

export function installNativeTooltipBridge(): void {
    if (installed || typeof document === 'undefined') return;
    installed = true;
    document.addEventListener('mouseover', handleOver, true);
    document.addEventListener('mouseout', handleOut, true);
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);
    // Keep the bubble glued to its anchor and honour the kill switch live
    scrollHandler = () => positionBridge();
    document.addEventListener('scroll', scrollHandler, {
        capture: true,
        passive: true,
    });
    window.addEventListener('resize', positionBridge);
    unsubscribeConfig = subscribeTooltipConfig(() => {
        if (getTooltipConfig().disabled) hideBridge();
    });
}

export function uninstallNativeTooltipBridge(): void {
    if (!installed) return;
    installed = false;
    hideBridge();
    if (bridgeEl !== null) {
        bridgeEl.remove();
        bridgeEl = null;
    }
    document.removeEventListener('mouseover', handleOver, true);
    document.removeEventListener('mouseout', handleOut, true);
    document.removeEventListener('focusin', handleFocusIn, true);
    document.removeEventListener('focusout', handleFocusOut, true);
    if (scrollHandler !== null) {
        document.removeEventListener('scroll', scrollHandler, true);
        scrollHandler = null;
    }
    window.removeEventListener('resize', positionBridge);
    if (unsubscribeConfig !== null) {
        unsubscribeConfig();
        unsubscribeConfig = null;
    }
}

// Self-arming: importing this module — directly, via the primitives, or a
// bare side-effect import in an app entry — retires the browser-default
// tooltip for the whole document. `uninstallNativeTooltipBridge()`
// restores stock browser behavior.
if (typeof document !== 'undefined') installNativeTooltipBridge();
